import { Button, CircularProgress, Divider, Grid } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { DepositFormSchema } from './create-deposit-form.schema'
import { Controller } from 'react-hook-form'
import { useCallback, useEffect, useState, useMemo } from 'react'
import useCreateReservationDeposit, { CreateReservationDepositInput } from '../../../../../api/create-reservation-deposit.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { FormItem } from 'hic-components'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import useCreateDepositForm from './create-deposit-form.hook'
import { Currency } from 'src/api/currency'
import { useSettings, useTenant } from 'src/app/hooks/context/context.hook'
import AdvanceFiscalTypeInput from './input/advance-fiscal-type-input'
import { ReservationF } from '../../../../../types/reservation-detail.types'
import DocumentCustomerInput from 'src/app/components/document-customer-input/document-customer-input'
import { CashDeskSimpleSelector } from 'src/app/components/cash-desk-input/cash-desk-simple-selector'
import PaymentSplitter from 'src/app/components/payment/payment-splitter/payment-splitter'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { CashDeskFragment, PaymentActivesQuery, PaymentTypeCode, useaccountantCategoriesQuery, useCashDesksQuery, usePaymentActivesQuery } from 'src/api/api.generated'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { useScelNoteHook } from 'src/app/pages/accounting/documents/checkout-document/components/co-receipt-note-hook'
import { PayByGateway } from 'src/app/pages/accounting/documents/components/pay-by-gateway/pay-by-gateway'
import { GatewayPaymentType, TsPayInfo } from 'src/app/pages/accounting/documents/components/pay-by-gateway/pay-by-gateway.types'
import PaymentsFormField from 'src/app/pages/accounting/documents/components/payments/payments-form-field'
import { v4 as uuidv4 } from 'uuid'
import VatsSelect from 'src/app/pages/common/customer/form/components/vats-select'
export type CreateDepositFormProps = {
  propertyId: string
  reservation: ReservationF
  open: boolean
  onClose: () => void
}

export default function CreateDepositForm(props: CreateDepositFormProps) {
  const { propertyId, open, onClose, reservation } = props
  const { id: tenantId } = useTenant()
  const { currency } = useTenant()
  const accounting = useSettings()?.accounting || null
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const showError = useErrorSnackBar()
  const { md } = useBreakpoints()
  const formatDate = useFormatDate()
  const { compileScelNote } = useScelNoteHook()

  const [dataForGatewayPayment, setDataForGatewayPayment] = useState<GatewayPaymentType>()

  const { data: paymentActives } = usePaymentActivesQuery({
    variables: {
      tenantId: tenantId,
      propertyId: propertyId,
    },
  })

  const unpadiPaymentActives = useMemo(() => {
    return paymentActives?.paymentActives.filter((pa) => pa.paymentType.code === PaymentTypeCode.UNPAID).map((i) => i.id)
  }, [paymentActives])

  const createDeposit = useCreateReservationDeposit(propertyId)

  const { data: cashDesksQuery, refetch: cashDesksQueryRefetch } = useCashDesksQuery({ skip: true })

  const [cashDeskId, setCashDeskId] = useState<string | null>(null)

  const [cashDesksOpened, setCashDesksOpened] = useState<CashDeskFragment[]>([])

  useEffect(() => {
    const fetchCashDesks = async () => {
      const cashDeskQueryResult = await cashDesksQueryRefetch({ tenantId, propertyId, open: true })
      if (!cashDeskQueryResult.error && cashDeskQueryResult.data && cashDeskQueryResult.data.cashDesks) {
        setCashDesksOpened(cashDeskQueryResult.data.cashDesks)
      }
    }
    if (tenantId && propertyId) fetchCashDesks()
  }, [tenantId, propertyId])

  const onSubmit = useCallback(
    async (data: DepositFormSchema) => {
      if (!cashDeskId) {
        return
      }
      const input = getCreateReservationDepositInput(reservation.id, currency, cashDeskId, data)
      if (input === null) {
        return
      }
      setLoading(true)
      const result = await createDeposit(input)
      if (result.result) {
        onClose()
      } else {
        showError(result.apolloError)
      }
      setLoading(false)
    },
    [onClose, reservation.id, currency, createDeposit, cashDeskId],
  )

  const { control, errors, handleSubmit, reset, watch, setValue } = useCreateDepositForm(onSubmit, { advanceFiscalType: accounting?.depositDocumentType }, unpadiPaymentActives)

  const formData = watch()

  useEffect(() => {
    reset()
  }, [open])

  /** Update scelNote */
  useEffect(() => {
    const customerName = watch('customer.name')
    const isReceipt = watch('advanceFiscalType') === 'RECEIPT'
    if (isReceipt && customerName) {
      const scelNote = compileScelNote({
        customer: { name: customerName },
        resource: {
          code: reservation.reservationCode,
        },
        reservation: {
          reservationReference: {
            mainGuest: {
              name: reservation.reference.mainGuest.name,
            },
            voucher: reservation.voucher ?? undefined,
          },
        },
        reservedResource: {
          start: reservation.reservedResources?.[0] ? formatDate(reservation.reservedResources[0].start) : undefined,
          end: reservation.reservedResources?.[0] ? formatDate(reservation.reservedResources[0].end) : undefined,
        },
      })
      if (scelNote) setValue('scelNote', scelNote)
    }
  }, [JSON.stringify(watch('customer.name')), JSON.stringify(watch('advanceFiscalType')), JSON.stringify(reservation)])

  const partialTsPayInfo = useMemo((): Omit<TsPayInfo, 'onPaymentConfirmed'> | undefined => {
    const paymentDescription = reservation.reservationCode
    const reservationId = reservation.id
    const customerEmail = watch('customer.email')
    const customerId = watch('customer.id')
    if (cashDeskId) {
      const cashDeskTypeId = cashDesksOpened?.find((c) => c.id === cashDeskId)?.cashDeskType.id ?? undefined
      if (paymentDescription && customerEmail && customerId && cashDeskTypeId)
        return {
          customerEmail,
          paymentDescription,
          reservationId,
          customerId,
          cashDeskTypeId,
        }
    }
  }, [propertyId, watch('customer.email'), watch('customer.id'), watch('customer.name'), reservation, cashDeskId, cashDesksOpened])

  const { data: accountantCategoriesData, loading: accountantCategoriesLoading } = useaccountantCategoriesQuery({
    variables: {
      filter: { tenantId: { eq: tenantId } },
    },
  })

  const defaultVatId = useMemo(() => {
    if (accountantCategoriesData?.accountantCategories) {
      const advanceFiscalType = watch('advanceFiscalType')
      // const {}
      if (advanceFiscalType && (advanceFiscalType === 'INVOICE' || advanceFiscalType === 'RECEIPT')) {
        return accountantCategoriesData.accountantCategories.find((i) => i.default.advance)?.defaultVatId
      } else {
        return accountantCategoriesData.accountantCategories.find((i) => i.default.deposit)?.defaultVatId
      }
    }
  }, [accountantCategoriesData, watch('advanceFiscalType')])

  const localLoading = useMemo(() => loading || accountantCategoriesLoading, [loading, accountantCategoriesLoading])

  return (
    <FormLayoutDrawer //
      title={t('pms.booking.reservation.create_deposit.title')}
      open={open}
      onClose={() => {
        if (!localLoading) onClose()
      }}
      width={md ? '60%' : '100%'}
      toolbar={
        <Grid container spacing={2} direction="row-reverse">
          <Grid item>
            <Button disabled={localLoading} variant="contained" onClick={handleSubmit} endIcon={localLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : undefined}>
              {t('pms.booking.reservation.create_deposit.confirm')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              disabled={localLoading}
              variant="outlined"
              color="error"
              onClick={() => {
                onClose()
              }}
            >
              {t('commons.cancel')}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={6} lg={4} xl={3}>
          <Controller<DepositFormSchema, 'amount'>
            name="amount"
            control={control}
            render={({ field }) => (
              <FormItem //
                fullWidth
                fieldLabel={t('pms.booking.reservation.create_deposit.amount')}
                errorMessage={errors?.amount?.message}
              >
                <HicAmountField //
                  value={field.value}
                  currency={currency}
                  onChange={field.onChange}
                  error={errors?.amount ? true : false}
                  disabled={localLoading}
                />
              </FormItem>
            )}
          />
        </Grid>
        <Grid item xs={6} lg={4} xl={3}>
          <Controller<DepositFormSchema, 'advanceFiscalType'>
            name="advanceFiscalType"
            control={control}
            render={({ field }) => (
              <FormItem //
                fullWidth
                fieldLabel={t('pms.booking.reservation.create_deposit.fiscal_type')}
                errorMessage={errors?.advanceFiscalType?.message}
              >
                <AdvanceFiscalTypeInput //
                  value={field.value || null}
                  onChange={field.onChange}
                  error={errors.advanceFiscalType ? true : false}
                />
              </FormItem>
            )}
          />
        </Grid>
        {formData.advanceFiscalType !== 'NON_FISCAL' && (
          <Grid item xs={6} lg={4} xl={3}>
            <Controller<DepositFormSchema, 'vatId'>
              name="vatId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <VatsSelect
                  value={value ?? defaultVatId ?? ''}
                  onChange={(newVat) => {
                    onChange(newVat)
                  }}
                  disabled={localLoading}
                  errorMessage={errors.vatId?.message}
                  size="medium"
                  label={t('commons.vat')}
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <CashDeskSimpleSelector
            value={cashDeskId}
            tenantId={tenantId}
            propertyId={propertyId}
            documentResourceTypes={[]}
            fiscalType={formData.advanceFiscalType}
            onCashDeskIdChanged={setCashDeskId}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider light />
        </Grid>

        <Grid item xs={12}>
          <Controller<DepositFormSchema, 'customer'> //
            name="customer"
            control={control}
            defaultValue={{
              id: '',
              countryCode: '',
              address: '',
              splitPayment: false,
            }}
            render={({ field }) => (
              <DocumentCustomerInput //
                referenceCustomers={reservation.customerReferenceList}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value)
                }}
                error={errors?.customer}
                customerCurrentData={null}
                syncData={false}
                dataSynched={() => {
                  console.log('synched')
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider light />
        </Grid>

        <Grid item xs={12}>
          <Controller<DepositFormSchema, 'payments'> //
            name="payments"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <PaymentsFormField
                paymentActives={paymentActives}
                fiscalType={formData.advanceFiscalType}
                amount={formData.amount ?? 0}
                value={field.value.map((row) => {
                  return {
                    uuid: uuidv4(), // id is required only to mantain order of documentPaymentDetails
                    ...row,
                    paymentActiveId: row.paymentActiveId ?? null,
                  }
                })}
                onChange={(value) => field.onChange(value)}
                error={errors.payments}
                depositCreation={true}
                dataForGatewayPayment={(amount, paymentActivesWithGateway, blockAmountChanges) => {
                  setDataForGatewayPayment({ amount, paymentActivesWithGateway, blockAmountChanges })
                }}
              />
            )}
          />
          {partialTsPayInfo && dataForGatewayPayment ? (
            <PayByGateway
              dataForGatewayPayment={dataForGatewayPayment}
              propertyId={propertyId}
              tsPayInfo={{
                ...partialTsPayInfo,
                onPaymentConfirmed: (success, paymentActiveId) => {
                  if (success && dataForGatewayPayment.amount) {
                    const prevPayments = watch('payments') ?? []
                    const updPayments = [{ amount: dataForGatewayPayment.amount ?? 0, paymentActiveId, editable: false }, ...prevPayments.filter((x) => x.paymentActiveId)]
                    setValue('payments', updPayments)
                  }
                },
              }}
              onLoading={setLoading}
            />
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </FormLayoutDrawer>
  )
}

function getCreateReservationDepositInput(reservationId: string, currency: Currency, cashDeskId: string, data: DepositFormSchema): CreateReservationDepositInput | null {
  return {
    amount: data.amount,
    reservationId: reservationId,
    cashDeskId: cashDeskId,
    customerId: data.customer.id,
    currency: currency,
    fiscalType: data.advanceFiscalType,
    payments: data.payments.map((p) => {
      const { editable, ...other } = p
      return other
    }),
    scelNote: data.scelNote,
    vatId: data.vatId,
  }
}
